import { useQuery } from "@tanstack/react-query";
import { createContext, type FC, type ReactNode, useCallback, useContext, useEffect, useMemo } from "react";
import { useTranslation } from "react-i18next";
import { useSearchParams } from "react-router-dom";
import { useAuth } from "../../../contexts/AuthProvider";
import { useAnalytics } from "../../../contexts/analytics/hooks";
import { useChosenDepartment } from "../../../contexts/ChosenDepartment";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { backendService } from "../../../services/backend.service";
import type { PbjNetworkBuilding } from "../../../utils/models";
import { multiFacilityDepartmentIds } from "../../ManagementCenter/MultiFacility/utils";
import { pbjCachedQueryOptions } from "../hooks/pbjQueryOptions";
import { PBJ_MODEL_QUERY_KEY } from "../hooks/usePbjModel";
import { useReplaceSearchParams } from "../hooks/useReplaceSearchParams";

const BUILDING_PARAM = "building";
/** Never part of a department id, so joining and splitting on it round-trips. */
const ID_SEPARATOR = "\n";
/** Nothing to list while the network is loading or for someone with one building. */
const NO_BUILDINGS: readonly PbjNetworkBuilding[] = [];

export type PbjNetwork = {
	/**
	 * The caller supervises two or more buildings in this network: Audit and Submission Sheets read every one,
	 * with the Building column and filter. Otherwise they read the header department only, as they always did.
	 */
	isNetwork: boolean;
	/** Every building in the network the caller supervises; empty while loading or for someone with one building. */
	networkBuildings: readonly PbjNetworkBuilding[];
	/** The buildings Audit and Submission Sheets read: every network building, or just the header department. */
	buildings: readonly PbjNetworkBuilding[];
	buildingName: (departmentId: string) => string;
	/** The zone a building's punch times read in. */
	buildingTimezone: (departmentId: string) => string;
	/** The header department: the one building whose rows can be edited, imported into, or configured. */
	homeDepartmentId: string;
	/** The shared Building filter, kept in the URL; empty means every building. */
	selectedBuildingIds: ReadonlySet<string>;
	setSelectedBuildingIds: (departmentIds: ReadonlySet<string>) => void;
};

const PbjNetworkContext = createContext<PbjNetwork | null>(null);

export const usePbjNetwork = (): PbjNetwork => {
	const network = useContext(PbjNetworkContext);
	if (!network) throw new Error("usePbjNetwork must be used inside PbjNetworkProvider");
	return network;
};

/** A failed building list hides the network view, so the supervisor is told and the failure is tracked. */
const useReportBuildingsFailure = (isError: boolean, error: Error | null, homeDepartmentId: string) => {
	const { t } = useTranslation();
	const { showSnackbar } = useSnackbar();
	const { trackError } = useAnalytics();
	useEffect(() => {
		if (!isError) return;
		showSnackbar(t("pbj_model.network.buildings_failed"), "error");
		trackError("pbj_network_buildings_error", { department_id: homeDepartmentId, error_message: String(error) });
	}, [isError, error, homeDepartmentId, showSnackbar, t, trackError]);
};

/** The server's building list, asked for only when the sign-in department list shows a possible network. */
const useNetworkBuildings = () => {
	const { chosenDepartment } = useChosenDepartment();
	const { userInfo } = useAuth();
	const homeDepartmentId = chosenDepartment.department_id;
	// The sign-in list answers "could this person have a network?" with no request; the server list then
	// drops folded sibling departments and nurse-only memberships.
	const mayHaveNetwork = multiFacilityDepartmentIds(userInfo.departments, chosenDepartment.hospital, homeDepartmentId).length > 1;
	const {
		data: serverBuildings,
		isError,
		error,
	} = useQuery<PbjNetworkBuilding[]>({
		queryKey: [PBJ_MODEL_QUERY_KEY, "network-buildings", homeDepartmentId],
		queryFn: async () => (await backendService.getPbjNetworkBuildings(homeDepartmentId)).data,
		enabled: mayHaveNetwork,
		...pbjCachedQueryOptions,
	});
	const isNetwork = mayHaveNetwork && serverBuildings !== undefined && serverBuildings.length > 1;
	const homeBuilding = useMemo<PbjNetworkBuilding>(
		() => ({ departmentId: homeDepartmentId, name: chosenDepartment.display_name, timezone: chosenDepartment.timezone }),
		[homeDepartmentId, chosenDepartment.display_name, chosenDepartment.timezone],
	);
	useReportBuildingsFailure(isError, error, homeDepartmentId);
	// DEFAULT: no network buildings until the list loads, for someone with one building, or after the list fails
	// to load (reported once, below) — the page then reads the header department only, as it always did.
	return { isNetwork, networkBuildings: isNetwork && serverBuildings ? serverBuildings : NO_BUILDINGS, homeBuilding };
};

/** Name and zone lookups over every building the page can mention, the header department always included. */
const useBuildingLookup = (networkBuildings: readonly PbjNetworkBuilding[], homeBuilding: PbjNetworkBuilding) => {
	// The header department is always known: a Dietary department folded under its main building isn't
	// a network building of its own, but the page still names it (downloads, the threshold label).
	const buildingById = useMemo(
		() => new Map([homeBuilding, ...networkBuildings].map((building) => [building.departmentId, building])),
		[homeBuilding, networkBuildings],
	);
	const buildingOf = useCallback(
		(departmentId: string) => {
			const building = buildingById.get(departmentId);
			if (building === undefined) throw new Error(`PBJ row belongs to building ${departmentId}, which is not in the network view`);
			return building;
		},
		[buildingById],
	);
	const buildingName = useCallback((departmentId: string) => buildingOf(departmentId).name, [buildingOf]);
	const buildingTimezone = useCallback((departmentId: string) => buildingOf(departmentId).timezone, [buildingOf]);
	return { buildingById, buildingName, buildingTimezone };
};

/** The Building filter, kept in the URL so every tab and a shared link agree. */
const useBuildingSelection = (knownBuildings: ReadonlyMap<string, PbjNetworkBuilding>) => {
	const [searchParams] = useSearchParams();
	const replaceSearchParams = useReplaceSearchParams();
	// Joined into one string so the set stays the same object across renders that don't change the URL.
	const urlBuildingIds = searchParams.getAll(BUILDING_PARAM).join(ID_SEPARATOR);
	// DEFAULT: ids that aren't in this network (left over from another network, or a shared link for buildings
	// the viewer doesn't supervise) are dropped rather than filtering every row away. If every id is dropped
	// the selection is empty, which means every building.
	const selectedBuildingIds = useMemo<ReadonlySet<string>>(
		() => new Set(urlBuildingIds.split(ID_SEPARATOR).filter((departmentId) => knownBuildings.has(departmentId))),
		[urlBuildingIds, knownBuildings],
	);
	const writeSelection = useCallback(
		(departmentIds: ReadonlySet<string>) =>
			replaceSearchParams((params) => {
				params.delete(BUILDING_PARAM);
				for (const departmentId of departmentIds) params.append(BUILDING_PARAM, departmentId);
			}),
		[replaceSearchParams],
	);
	return { selectedBuildingIds, writeSelection };
};

/** The PBJ page's network view state: which buildings the data tabs read and the shared Building filter. */
export const PbjNetworkProvider: FC<{ children: ReactNode }> = ({ children }) => {
	const { track } = useAnalytics();
	const { isNetwork, networkBuildings, homeBuilding } = useNetworkBuildings();
	const { buildingById, buildingName, buildingTimezone } = useBuildingLookup(networkBuildings, homeBuilding);
	const { selectedBuildingIds, writeSelection } = useBuildingSelection(buildingById);
	const homeDepartmentId = homeBuilding.departmentId;
	const buildings = useMemo(() => (isNetwork ? networkBuildings : [homeBuilding]), [isNetwork, networkBuildings, homeBuilding]);

	const setSelectedBuildingIds = useCallback(
		(departmentIds: ReadonlySet<string>) => {
			writeSelection(departmentIds);
			track("pbj_building_filter_changed", { department_id: homeDepartmentId, selected_count: departmentIds.size, building_count: buildings.length });
		},
		[writeSelection, track, homeDepartmentId, buildings.length],
	);

	const value = useMemo<PbjNetwork>(
		() => ({
			isNetwork,
			networkBuildings,
			buildings,
			buildingName,
			buildingTimezone,
			homeDepartmentId,
			selectedBuildingIds,
			setSelectedBuildingIds,
		}),
		[isNetwork, networkBuildings, buildings, buildingName, buildingTimezone, homeDepartmentId, selectedBuildingIds, setSelectedBuildingIds],
	);
	return <PbjNetworkContext.Provider value={value}>{children}</PbjNetworkContext.Provider>;
};
